import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { yieldToMainThread } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {HTMLElement | null} */
  #variantPicker = null;

  /** @type {string | null} */
  #requestedVariantId = null;

  /** @type {string | null} */
  #previousVariantId = null;

  /** @type {string} */
  #pendingColorName = '';

  connectedCallback() {
    super.connectedCallback();

    if (this.#controller === null || this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.#isLeatherColorSwipe && target && !this.closest('.quick-add-modal__content')) {
      const productId = this.dataset.mhProductId;
      const variantPicker = Array.from(
        target.querySelectorAll('variant-picker[data-template-product-match="true"]')
      ).find((picker) => picker instanceof HTMLElement && picker.dataset.productId === productId);

      if (variantPicker instanceof HTMLElement && this.slideshow) {
        this.#variantPicker = variantPicker;
        this.slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleLeatherSlideSelect, { signal });
        target.addEventListener('variant:fetch-error', this.#handleLeatherVariantFetchError, { signal });
        variantPicker.setAttribute('data-mh-gallery-controlled', 'true');
        this.dataset.mhSwipeReady = 'true';
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#variantPicker?.removeAttribute('data-mh-gallery-controlled');
    this.#variantPicker = null;
    this.#requestedVariantId = null;
    this.#previousVariantId = null;
    this.#pendingColorName = '';
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (this.#isLeatherColorSwipe) {
      if (String(event.detail.data.productId) !== String(this.dataset.mhProductId)) return;

      const mediaId = event.detail.resource?.featured_media?.id;
      const colorName = this.#pendingColorName;

      this.#requestedVariantId = null;
      this.#previousVariantId = null;
      this.#pendingColorName = '';

      if (mediaId) {
        this.slideshow?.select({ id: String(mediaId) }, undefined, { animate: false });
      }

      const liveRegion = this.querySelector('[data-mh-color-live]');
      if (liveRegion instanceof HTMLElement && colorName) {
        liveRegion.textContent = `${colorName} rengi seçildi.`;
      }

      return;
    }

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Selects the exact Shopify color variant represented by a user-swiped hero slide.
   * Detail and editorial slides deliberately have no variant ID and therefore do nothing.
   *
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleLeatherSlideSelect = (event) => {
    if (!event.detail.userInitiated || event.target !== this.slideshow) return;

    const slide = event.detail.slide;
    if (!(slide instanceof HTMLElement) || !this.slideshow?.contains(slide)) return;

    this.querySelector('[data-mh-swipe-cue]')?.setAttribute('hidden', '');

    const variantId = slide.dataset.mhVariantId;
    if (!variantId || !this.#variantPicker) return;

    const input = Array.from(this.#variantPicker.querySelectorAll('input[type="radio"][data-variant-id]')).find(
      (candidate) => candidate instanceof HTMLInputElement && candidate.dataset.variantId === variantId
    );

    if (!(input instanceof HTMLInputElement)) return;
    if (input.checked || input.disabled) return;
    if (this.#requestedVariantId === variantId) return;

    const previousInput = this.#variantPicker.querySelector('input[type="radio"][data-variant-id]:checked');

    if (!this.#requestedVariantId) {
      this.#previousVariantId =
        previousInput instanceof HTMLInputElement ? previousInput.dataset.variantId || null : null;
    }
    this.#requestedVariantId = variantId;
    this.#pendingColorName = slide.dataset.mhColorName || input.value;
    input.click();
  };

  /**
   * Restores the last confirmed variant and reveals the real picker if a gallery-driven fetch fails.
   * This keeps the displayed color, URL and purchase form aligned instead of leaving a pending color selected.
   *
   * @param {CustomEvent<{ productId?: string }>} event - The variant fetch error event.
   */
  #handleLeatherVariantFetchError = (event) => {
    if (!this.#requestedVariantId) return;
    if (String(event.detail?.productId) !== String(this.dataset.mhProductId)) return;

    const failedVariantId = this.#requestedVariantId;
    const previousVariantId = this.#previousVariantId;
    const picker = /** @type {HTMLElement & { updateSelectedOption?: (target: Element) => void }} */ (
      this.#variantPicker
    );

    if (picker && previousVariantId) {
      const previousInput = Array.from(picker.querySelectorAll('input[type="radio"][data-variant-id]')).find(
        (candidate) => candidate instanceof HTMLInputElement && candidate.dataset.variantId === previousVariantId
      );

      if (previousInput instanceof HTMLInputElement) {
        if (typeof picker.updateSelectedOption === 'function') {
          picker.updateSelectedOption(previousInput);
        } else {
          previousInput.checked = true;
        }

        const previousSlide = this.slideshow?.querySelector(`[data-mh-variant-id="${CSS.escape(previousVariantId)}"]`);
        const previousMediaId = previousSlide instanceof HTMLElement ? previousSlide.dataset.mhMediaId : null;
        if (previousMediaId) {
          this.slideshow?.select({ id: previousMediaId }, undefined, { animate: false });
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', previousVariantId);
        history.replaceState({}, '', url.toString());
      }
    }

    this.#requestedVariantId = null;
    this.#previousVariantId = null;
    this.#pendingColorName = '';
    picker?.removeAttribute('data-mh-gallery-controlled');
    delete this.dataset.mhSwipeReady;
    this.querySelector('[data-mh-swipe-cue]')?.setAttribute('hidden', '');

    const liveRegion = this.querySelector('[data-mh-color-live]');
    if (liveRegion instanceof HTMLElement) {
      liveRegion.textContent = 'Renk seçimi güncellenemedi. Lütfen renk seçeneğini yeniden deneyin.';
    }

    // VariantPicker schedules its URL update after yielding to the main thread. If the fetch
    // fails before that callback runs, it can briefly overwrite the immediate recovery URL.
    // Restore it once more only while the failed request is still the URL's active variant,
    // the confirmed radio is unchanged, and no newer gallery request has started.
    if (picker && previousVariantId) {
      yieldToMainThread().then(() => {
        if (!this.isConnected || this.#requestedVariantId) return;

        const confirmedInput = picker.querySelector('input[type="radio"][data-variant-id]:checked');
        if (!(confirmedInput instanceof HTMLInputElement)) return;
        if (confirmedInput.dataset.variantId !== previousVariantId) return;

        const url = new URL(window.location.href);
        if (url.searchParams.get('variant') !== failedVariantId) return;

        url.searchParams.set('variant', previousVariantId);
        history.replaceState({}, '', url.toString());
      });
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  get #isLeatherColorSwipe() {
    return this.dataset.mhLeatherColorSwipe === 'true';
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
